import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import type { PlanLegInput } from '@jupi/shared'
import { IconButton, useSnackbar } from '@jupi/ui'
import { useReportingPoints, useWaypoints } from '../../api/plan'
import { legFromReportingPoint, legFromWaypoint, reportingPointName, searchReportingPoints } from './planLegs'
import { useClubLocation } from '@jupi/ui'
import { useNearMe } from './useNearMe'

// Choosing the next point of the plan: the whole screen on a phone, a
// search box that answers as the pilot types, and "nearest" for when they
// are standing at the field they are leaving from. A search also finds
// the AIP's reporting points (ENR 4.4 and VRPs), listed after the
// waypoints; they are not waypoint rows, so "nearest" leaves them out.
export default function WaypointPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (leg: PlanLegInput) => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { showSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const nearMe = useNearMe(() => showSnackbar(t('plan.waypoints.locationDenied'), { severity: 'warning' }))

  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 200)
    return () => clearTimeout(timer)
  }, [search])

  const { data: waypoints = [], isFetching } = useWaypoints({
    q: q || undefined,
    near: nearMe.position ?? undefined,
  })
  const shown = waypoints.slice(0, 50)
  // For a reporting point's magnetic variation: the full list, cached.
  const { data: allWaypoints = [] } = useWaypoints()
  const { data: reportingPoints = [] } = useReportingPoints(open)
  const home = useClubLocation()
  const homeWaypoint = allWaypoints.find((w) => w.code === home.code)
  const points = nearMe.position ? [] : searchReportingPoints(reportingPoints, q)

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1 }}>{t('plan.editor.addWaypoint')}</Box>
        <IconButton aria-label={t('common.close')} onClick={onClose}>
          <Icon icon="mdi:close" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
        <Stack spacing={1} sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', pb: 1, pt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('plan.waypoints.search')}
            slotProps={{
              htmlInput: { autoCapitalize: 'characters', autoComplete: 'off', enterKeyHint: 'search' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="mdi:magnify" />
                  </InputAdornment>
                ),
                endAdornment: isFetching ? <CircularProgress size={18} /> : null,
              },
            }}
          />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {homeWaypoint && (
              <Button variant="outlined" startIcon={<Icon icon="mdi:home-map-marker" />} onClick={() => onPick(legFromWaypoint(homeWaypoint))}>
                {[homeWaypoint.code, homeWaypoint.name].filter(Boolean).join(' ')}
              </Button>
            )}
            <Button
              variant={nearMe.position ? 'contained' : 'outlined'}
              startIcon={<Icon icon="mdi:satellite-variant" />}
              onClick={nearMe.toggle}
              disabled={nearMe.locating}
            >
              {nearMe.position ? t('plan.waypoints.nearMeOff') : t('plan.waypoints.nearMe')}
            </Button>
          </Stack>
        </Stack>
        {shown.length === 0 && points.length === 0 && !isFetching ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {t('plan.waypoints.none')}
          </Typography>
        ) : (
          <List disablePadding>
            {shown.map((w) => (
              <ListItemButton key={w.code} onClick={() => onPick(legFromWaypoint(w))} sx={{ minHeight: 56 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{w.code}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {w.name}
                      </Typography>
                    </Stack>
                  }
                  secondary={w.com ? `COM ${w.com}` : null}
                />
                {w.distanceNm !== null ? (
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {w.distanceNm < 10 ? w.distanceNm.toFixed(1) : Math.round(w.distanceNm)} NM
                  </Typography>
                ) : null}
              </ListItemButton>
            ))}
            {points.length > 0 && <ListSubheader disableGutters>{t('plan.waypoints.reportingPoints')}</ListSubheader>}
            {points.map((p) => (
              <ListItemButton key={p.id} onClick={() => onPick(legFromReportingPoint(p, allWaypoints))} sx={{ minHeight: 56 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{p.code}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {reportingPointName(p)}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  )
}
